import { DEFAULT_SETTINGS, type SettingChange, type SettingKey } from "@serophin/nottldr-shared";
import { useSettings } from "@serophin/nottldr-shared/hooks";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";

export type SettingsState = {
  /** The saved value of a setting, or its default. */
  get: (key: SettingKey) => string;
  /** Shows the new value at once, saves it, and puts the old one back when saving fails. */
  change: (changes: SettingChange[]) => void;
};

/** Settings as the panels use them. They work signed out too: the client keeps them in the cache then. */
export const useSettingsState = (): SettingsState => {
  const { settings, setSettings } = useSettings();
  const [pending, setPending] = useState<Partial<Record<SettingKey, string>>>({});
  const toast = useToast();

  const get = (key: SettingKey): string => pending[key] ?? settings[key] ?? DEFAULT_SETTINGS[key];

  const change = (changes: SettingChange[]): void => {
    setPending((previous) => ({ ...previous, ...Object.fromEntries(changes.map(({ key, value }) => [key, value])) }));
    // A saved value stays in `pending` (it is what the cache holds now); a failed one is dropped, which restores the old value.
    setSettings(changes).catch((error: unknown) => {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings. Please try again.");
      setPending((previous) => {
        const next = { ...previous };
        for (const { key } of changes) {
          delete next[key];
        }
        return next;
      });
    });
  };

  return { get, change };
};
